import { useEffect, useRef, useState } from 'react'
import { ClapboardClosed } from './ClapboardIcon'
import { Z } from '../lib/zLayers'
import { railBusy, claimRail, releaseRail, subscribeRail } from '../lib/toastRail'
import { isOverlayBusy } from '../lib/overlayBusy'
import { beginCapture } from '../lib/batchReceipt'

const RAIL_ID = 'undo'
const VISIBLE_MS = 3200   // how long the bar stays up — counted only while it's actually on screen

// ── UndoToast — the instant "you just watched an episode" confirmation ────────
// Every mark-watched surface in the app (My List rows, Tonight plans, Home Up
// Next, Calendar, ShowDetail) funnels through useWatchlist.saveProgress, which
// dispatches nextep:progress-marked with { showId, watchedEp, showName, undo }.
// This component listens for that event and shows the ONE thing that has to be
// instant and unmissable: ✓ confirm the episode, ↩ undo it. No matter where the
// tap happened it looks and behaves identically.
//
// v2 (2026-08-04): rating + mood used to live in THIS toast as sequential
// "steps," each re-arming the same 5s auto-dismiss. That worked for one mark at
// a time, but binge-marking several episodes in quick succession (Jason,
// rewatching South Park — E3, E4, E5, E6 within seconds) called showNow() again
// on the SAME instance for each new mark, silently overwriting whatever step
// was mid-tap. The rate/mood ask briefly lived in a separate queued sheet
// (PostWatchSheet), retired 2026-08-15 in favor of Episode Pulse's own
// independent post-watch peek (nextep:episode-watched, fired straight from
// useWatchlist — not routed through this toast at all) as the one and only
// rating/reaction/note surface. This toast owns nothing but the ✓/Undo bar
// and the bottom rail.
export function UndoToast() {
  const [toast, setToast]   = useState(null)
  const [, setRailGate]     = useState(0)   // re-check when the rail frees up
  const [leaving, setLeaving] = useState(false)
  const hideTimer  = useRef(null)
  const leaveTimer = useRef(null)
  const toastRef   = useRef(null)

  const dismiss = (delay = 0) => {
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => {
      setLeaving(true)
      leaveTimer.current = setTimeout(closeToast, 220)
    }, delay)
  }

  const closeToast = () => {
    clearTimeout(hideTimer.current); clearTimeout(leaveTimer.current)
    toastRef.current = null
    setToast(null); setLeaving(false)
  }

  const showNow = (d) => {
    toastRef.current = d
    clearTimeout(hideTimer.current); clearTimeout(leaveTimer.current)
    setLeaving(false)
    setToast(d)
    // No dismiss timer here — the countdown below only runs while we're visible.
  }

  useEffect(() => {
    const onMark = (e) => {
      if (!e.detail?.watchedEp || typeof e.detail.undo !== 'function') return
      // A cascade opens a receipt session instead (lib/batchReceipt): the Undo
      // lives on that sheet with no clock. A thin cascade replays this event.
      if (beginCapture(e.detail)) return
      showNow(e.detail)   // instant — this toast only ever owns the ✓/Undo bar
      // The rate/mood/note moment is Episode Pulse's own peek, fired independently
      // from useWatchlist's nextep:episode-watched — nothing to hand off here.
    }
    window.addEventListener('nextep:progress-marked', onMark)
    return () => {
      window.removeEventListener('nextep:progress-marked', onMark)
      clearTimeout(hideTimer.current); clearTimeout(leaveTimer.current)
    }
  }, [])

  // Bottom-rail single occupancy. UndoToast sits at bottom:78px, between the Episode
  // Pulse peek (72) and the rating toast (82) — marking a LIST watched, then a single
  // show, then a pulse landing put three of these on top of each other
  // (Jason 2026-07-27). First one to claim shows; the rest stand down.
  //
  // ...and a full-screen moment (an achievement medallion, a streak milestone, a
  // wrap cinematic) sits ABOVE this at z-celebration with a 94%-opaque scrim. The
  // bar used to render underneath it with its 3.2s expiry still ticking, so a
  // mark that also minted an achievement showed the medallion and then... nothing.
  // No Undo, ever (Jason 2026-09-20: "did not prompt me for an undo option then
  // fired an achievement"). Now: stand down while an overlay owns the screen, and
  // only count visible time toward the expiry — same accounting Episode Pulse's
  // peek already does for the wrap cinematic.
  const railHeld = !!toast && railBusy(RAIL_ID)
  const [, setOverlayGate] = useState(0)
  const overlayHeld = !!toast && isOverlayBusy()
  const held = railHeld || overlayHeld
  useEffect(() => {
    if (toast && !held) claimRail(RAIL_ID); else releaseRail(RAIL_ID)
    return () => releaseRail(RAIL_ID)
  }, [toast, held])
  useEffect(() => subscribeRail(() => setRailGate(g => g + 1)), [])

  const visibleMsRef = useRef(0)
  useEffect(() => {
    if (!toast) return
    visibleMsRef.current = 0
    let last = Date.now(), wasHeld = null
    const iv = setInterval(() => {
      const now = Date.now(); const el = now - last; last = now
      const h = railBusy(RAIL_ID) || isOverlayBusy()
      if (h !== wasHeld) { wasHeld = h; setOverlayGate(g => g + 1) }
      if (h) return
      visibleMsRef.current += el
      if (visibleMsRef.current >= VISIBLE_MS) { clearInterval(iv); dismiss(0) }
    }, 100)
    return () => clearInterval(iv)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast])

  if (!toast || held) return null
  const ep = toast.watchedEp
  // A cascade mark ("through here") covers more than the endpoint — say the real
  // count instead of just S{season}·E{episode} (2026-09-17, the count now rides
  // along on nextep:progress-marked so this is the ONE confirmation for both cases).
  const multi = toast.count > 1

  const handleUndo = () => {
    try { toast.undo() } catch { /* never break on undo */ }
    // toast.undo() itself dispatches nextep:episode-unwatched (useWatchlist), which
    // Episode Pulse already listens for to cancel its own peek/sheet — nothing to
    // hand off here.
    closeToast()
  }

  return (
    <div role="status" aria-live="polite" style={{
      position:'fixed', left:'50%', bottom:'calc(env(safe-area-inset-bottom) + 78px)',
      zIndex:Z.toast, display:'flex', flexDirection:'column', gap:8,
      background:'linear-gradient(135deg, color-mix(in srgb, var(--state-good) 14%, #14141f), color-mix(in srgb, var(--state-good) 6%, #0d0d16))',
      border:'1px solid color-mix(in srgb, var(--state-good) 40%, transparent)',
      borderRadius:'var(--toast-radius)', padding:'11px 16px 12px',
      boxShadow:'0 14px 38px rgba(0,0,0,0.55), 0 4px 18px color-mix(in srgb, var(--state-good) 18%, transparent), inset 0 1px 0 rgba(var(--ink-rgb),0.06)',
      backdropFilter:'blur(16px) saturate(1.3)', WebkitBackdropFilter:'blur(16px) saturate(1.3)',
      fontFamily:'var(--font)', maxWidth:'calc(100vw - 32px)',
      '--nxa':(leaving ? 'undoOut 0.22s ease both' : 'undoIn 0.3s cubic-bezier(0.22,1.2,0.36,1) both'),
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        {/* The clapboard, not a tick: marking is slating a take everywhere in the app
            (deck legend, swipe overlay, confirm chip), so the Undo bar matches. */}
        <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:'var(--toast-chip-size)', height:'var(--toast-chip-size)', borderRadius:'var(--radius-full)', background:'rgba(var(--amber-rgb),0.14)', border:'1px solid rgba(var(--amber-rgb),0.35)', flexShrink:0 }}><ClapboardClosed size={20} color="var(--amber)" /></span>
        <span style={{ minWidth:0, overflow:'hidden' }}>
          <span style={{ display:'block', color:'var(--amber)', fontSize:'var(--toast-eyebrow-size)', fontWeight:'var(--toast-eyebrow-weight)', letterSpacing:'var(--toast-eyebrow-tracking)', textTransform:'uppercase', marginBottom:2 }}>Watched</span>
          <span style={{ display:'block', color:'#eeeeff', fontSize:13.5, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {multi ? `S${ep.season} · ${toast.count} episodes` : `S${ep.season}·E${ep.episode}`}{toast.showName ? ` · ${toast.showName}` : ''}
          </span>
        </span>
        <button onClick={handleUndo} style={{
          flexShrink:0, marginLeft:'auto', background:'rgba(var(--amber-rgb),0.12)', border:'1px solid rgba(var(--amber-rgb),0.3)',
          borderRadius:9, color:'var(--amber)', fontWeight:700, fontSize:12,
          padding:'6px 12px', cursor:'pointer', fontFamily:'var(--font)',
        }}>Undo</button>
      </div>
      <style>{'@keyframes undoIn{0%{opacity:0;transform:translate(-50%,14px);filter:blur(3px)}55%{filter:blur(0)}100%{opacity:1;transform:translate(-50%,0);filter:blur(0)}}@keyframes undoOut{from{opacity:1;transform:translate(-50%,0)}to{opacity:0;transform:translate(-50%,8px)}}'}</style>
    </div>
  )
}
